import type { Country } from "~/models/country";
import type { Genre } from "~/models/genre";
import type { CollaboratorInArtist, InvitedCollaborator } from "./collaborator";
import type { LabelCore } from "./label";
import type { ILink } from "./link";

export type ArtistUrl = {
  platformId: number;
  platformName: string;
  url: string;
};

export type ArtistStatus = {
  active: boolean;
  id: number;
  name: string;
};

export type ArtistBase = {
  avatarPath: string | null;
  heroPath: string | null;
  initials: string;
  stageName: string;
  uid: string;
};

// Used when other types reference an artist. For example labels, revenue shares, etc.
export type ArtistCore = ArtistBase & {
  createdAt: string;
  slug: string;
  status: ArtistStatus;
};

export type ArtistFanzone = ArtistBase & {
  biography: string | null;
  country: Country;
  genre: Genre | null;
  heroPath: string | null;
  links: Array<ILink>;
};

export type ArtistDetails = ArtistBase & {
  artistLabel: LabelCore;
  artistInvitedUsers: Array<InvitedCollaborator>;
  artistUsers: Array<CollaboratorInArtist>;
  biography: string | null;
  country: Country;
  distributionLinks: Array<ILink>;
  genre: Genre | null;
  isni: string | null;
  labelInvitedUsers: Array<InvitedCollaborator>;
  labelUsers: Array<CollaboratorInArtist>;
  socialLinks: Array<ILink>;
};

export type ArtistInList = ArtistBase & {
  avatarPath: string | null;
  created: string;
  heroPath: string | null;
  initials: string;
  label: LabelCore;
  slug: string;
  stageName: string;
  status: ArtistStatus;
  uid: string;
  users: Array<Omit<CollaboratorInArtist, "role">>;
};

export function getArtistPlatformSlug(url: string | undefined) {
  if (!url) {
    return null;
  }

  if (url.includes("music.amazon.com/")) {
    return new URL(url).pathname.split("/").pop();
  }
  if (url.includes("music.apple.com/")) {
    return new URL(url).pathname.split("/")[3];
  }
  if (url.includes("facebook.com/")) {
    return new URL(url).pathname.split("/")[1];
  }
  if (url.includes("instagram.com/")) {
    return new URL(url).pathname.split("/")[1];
  }
  if (url.includes("soundcloud.com/")) {
    return new URL(url).pathname.split("/")[1];
  }
  if (url.includes("tiktok.com/")) {
    return new URL(url).pathname.split("/")[1];
  }
  if (url.includes("twitter.com/") || url.includes("x.com/")) {
    return `@${new URL(url).pathname.split("/")[1]}`;
  }
  if (url.includes("youtube.com/")) {
    const maybeArtist = new URL(url).pathname.split("/")[1];
    return maybeArtist === "channel" ? null : maybeArtist;
  }

  return null;
}
